import { newId } from '@blagodar/shared';
import { api, ApiError, loadAuth, NetworkError } from '../api/client';
import { describeApiError } from '../api/errors';
import { db, type PendingCardOp } from './db';

/**
 * Расходки, сохранённые без связи (07.10.2026) — очередь и её отправка.
 *
 * «Отправить» без связи кладёт расходку сюда, и человек сразу видит, что
 * она сохранена и уйдёт сама. Уходит она вместе с очередью замеров
 * (`flushOutbox`): при появлении сети, при возврате в приложение и раз в
 * минуту.
 *
 * МОЛЧАНИЕ СЕРВЕРА — НЕ ОТКАЗ. Нет связи, сервер перезапускается, вход
 * истёк, «слишком часто» — расходка ждёт следующего раза (правило 86).
 * Отказ ПО ДЕЛУ (на смену уже есть расходка, адрес сняли) — сама она
 * больше не шлётся: тот же запрос получит тот же ответ. Человек видит
 * причину словами (правило 179) и решает: открыть набранное и поправить,
 * повторить или убрать.
 */

export type CardOpEvent =
  | { type: 'sent'; op: PendingCardOp; number: number | null; conflicts: string[] }
  | { type: 'refused'; op: PendingCardOp; error: string }
  | { type: 'changed' };

const listeners = new Set<(event: CardOpEvent) => void>();

export function onCardOps(listener: (event: CardOpEvent) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(event: CardOpEvent) {
  listeners.forEach((l) => l(event));
}

function me(): string | null {
  try {
    return loadAuth()?.user.id ?? null;
  } catch {
    return null;
  }
}

/** Своя операция: заведена этим человеком (без метки — тоже его, как у замеров) */
function own(op: PendingCardOp, userId: string | null): boolean {
  return !op.userId || op.userId === userId;
}

/** Расходки этого человека в очереди — старые первыми */
export async function listCardOps(userId: string | null = me()): Promise<PendingCardOp[]> {
  try {
    return (await db.cardOps.toArray()).filter((o) => own(o, userId)).sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

/** Новая расходка без связи — в очередь с номером, придуманным телефоном */
export async function queueCardCreate(op: {
  cardId: string;
  body: Record<string, unknown>;
  form: PendingCardOp['form'];
  submit: boolean;
  label: string;
}): Promise<void> {
  await db.cardOps.put({
    id: newId(),
    kind: 'create',
    cardId: op.cardId,
    body: op.body,
    form: op.form,
    submit: op.submit,
    userId: me(),
    label: op.label,
    createdAt: Date.now(),
    refusedAt: null,
    lastError: null,
  });
  emit({ type: 'changed' });
}

/**
 * Правка заведённой расходки без связи. Тела нет: набранное лежит
 * черновиком на устройстве, и уйдёт тот, что там к минуте отправки.
 * Прежняя правка той же расходки этим же человеком вытесняется — черновик
 * один, и «отправить на склад» не теряется, если его уже просили.
 */
export async function queueCardUpdate(op: { cardId: string; submit: boolean; label: string }): Promise<void> {
  const userId = me();
  await db.transaction('rw', db.cardOps, async () => {
    const older = (await db.cardOps.where('cardId').equals(op.cardId).toArray()).filter(
      (o) => o.kind === 'update' && own(o, userId),
    );
    if (older.length > 0) await db.cardOps.bulkDelete(older.map((o) => o.id));
    await db.cardOps.put({
      id: newId(),
      kind: 'update',
      cardId: op.cardId,
      submit: op.submit || older.some((o) => o.submit),
      userId,
      label: op.label,
      createdAt: Date.now(),
      refusedAt: null,
      lastError: null,
    });
  });
  emit({ type: 'changed' });
}

/** Человек убрал расходку из очереди или забрал набранное на экран */
export async function dropCardOp(id: string): Promise<void> {
  await db.cardOps.delete(id);
  emit({ type: 'changed' });
}

/**
 * Правку этой расходки сохранили со связью — ждущая в очереди устарела:
 * отправленная позже, она вернула бы прежние числа поверх новых.
 */
export async function dropCardUpdates(cardId: string): Promise<void> {
  try {
    const userId = me();
    const stale = (await db.cardOps.where('cardId').equals(cardId).toArray()).filter(
      (o) => o.kind === 'update' && own(o, userId),
    );
    if (stale.length === 0) return;
    await db.cardOps.bulkDelete(stale.map((o) => o.id));
    emit({ type: 'changed' });
  } catch {
    // Памяти нет — и очереди нет
  }
}

/** «Повторить» после отказа: человек поправил или решил попробовать ещё раз */
export async function retryCardOp(id: string): Promise<void> {
  const op = await db.cardOps.get(id);
  if (!op) return;
  await db.cardOps.put({ ...op, refusedAt: null, lastError: null });
  emit({ type: 'changed' });
  void flushCardOps();
}

/** Ответы, которые значат «не сейчас», а не «нет» (правило 86) */
const NOT_NOW = new Set([401, 408, 429]);

/**
 * Отказ по делу — словами, без шлюза: его читает монтажник. `final` — повтор
 * не поможет никогда: расходку выдали или отменили (правило 115)
 */
export class CardOpRefusal extends Error {
  constructor(
    message: string,
    readonly final = false,
  ) {
    super(message);
  }
}

function notNow(error: unknown, op: PendingCardOp): boolean {
  if (error instanceof NetworkError) return true;
  if (error instanceof ApiError) {
    if (NOT_NOW.has(error.status) || error.status >= 500) return true;
    // Правку поправили между чтением и записью — сведётся следующим разом
    if (op.kind === 'update' && error.status === 409) return true;
  }
  return false;
}

async function sendCreate(op: PendingCardOp): Promise<{ number: number | null; conflicts: string[] }> {
  const card = await api.post<{ id: string; number?: number }>('/consumables/cards', { ...op.body, id: op.cardId });
  if (op.submit) await api.post(`/consumables/cards/${op.cardId}/submit`, {});
  return { number: card?.number ?? null, conflicts: [] };
}

async function sendUpdate(op: PendingCardOp): Promise<{ number: number | null; conflicts: string[] }> {
  // Сводка строк живёт рядом с формой карточки — грузится, только когда есть что сводить
  const { sendQueuedEdit } = await import('./card-edit-send');
  return sendQueuedEdit(op);
}

/**
 * Идёт ли отправка: флаг ставится первой строкой, до любого ожидания, —
 * «сеть появилась» и «вкладка видна» приходят подряд (правило 114).
 */
let flushing = false;

export function cardOpsFlushing(): boolean {
  return flushing;
}

export async function flushCardOps(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    const user = me();
    if (!user) return;
    const queue = (await listCardOps(user)).filter((o) => o.refusedAt === null);
    for (const op of queue) {
      // Пока уходила очередь, планшет могли передать другому: чужой вход — стоп
      if (me() !== user) break;
      // Человек мог забрать её на экран, пока уходили предыдущие
      if (!(await db.cardOps.get(op.id))) continue;
      try {
        const sent = op.kind === 'create' ? await sendCreate(op) : await sendUpdate(op);
        await db.cardOps.delete(op.id);
        emit({ type: 'sent', op, ...sent });
      } catch (error) {
        if (notNow(error, op)) break;
        const message = error instanceof CardOpRefusal ? error.message : describeApiError(error);
        const final = error instanceof CardOpRefusal && error.final;
        await db.cardOps.put({ ...op, refusedAt: Date.now(), lastError: message, ...(final ? { final } : {}) });
        emit({ type: 'refused', op, error: message });
      }
    }
  } catch {
    // Памяти нет — нечего и отправлять
  } finally {
    flushing = false;
    emit({ type: 'changed' });
  }
}

/** Сколько своих ждёт и сколько получили отказ — для значка связи */
export async function countCardOps(userId: string | null = me()): Promise<{ pending: number; refused: number }> {
  const ops = await listCardOps(userId);
  return {
    pending: ops.filter((o) => o.refusedAt === null).length,
    refused: ops.filter((o) => o.refusedAt !== null).length,
  };
}
